import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || '';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      async (config) => {
        try {
          // Check if user is authenticated before attempting to get session
          await getCurrentUser();

          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();

          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            logger.debug('Auth token added to request', {
              method: config.method?.toUpperCase(),
              url: config.url
            });
          }
        } catch (error) {
          // User is not authenticated or session fetch failed - continue without token
          // This is normal for public endpoints
          logger.debug('Making unauthenticated request', {
            method: config.method?.toUpperCase(),
            url: config.url
          });
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        logger.debug('API Response', {
          status: response.status,
          url: response.config.url
        });
        return response;
      },
      (error) => {
        // Log only safe metadata. Never log response/request bodies or headers —
        // they can carry Authorization tokens and other sensitive data.
        if (error.response) {
          logger.error('API error response', undefined, {
            status: error.response.status,
            url: error.config?.url,
            message: error.message
          });
        } else if (error.request) {
          logger.error('No response received', undefined, {
            url: error.config?.url,
            message: error.message
          });
        } else {
          logger.error('Request setup error', undefined, {
            message: error.message
          });
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<{ data: T }>(url, config);
    return response.data.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<{ data: T }>(url, data, config);
    return response.data.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<{ data: T }>(url, data, config);
    return response.data.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<{ data: T }>(url, config);
    return response.data.data;
  }
}

export const api = new ApiService();
export default api;
